/**
 * GeoTypeSelectorGroup Helper Functions
 *
 * Provides helper functions for detecting unsupported files and categorizing
 * geodata parsing errors in the GeoDataImportForm.
 */

import JSZip from "jszip";
import { GeoJSONDataMap } from "../../../../../../utilities/types";
import { autoGuessPropertyMappings } from "../PropertyMappingSelector/PropertyMappingSelector.helpers";
import { AggregatedAutoGuessResult, PropertyWithExamples } from "./GeoTypeSelectorGroup.types";

/**
 * List of file extensions that are supported for geodata import.
 * This is the single source of truth - matches GeoTypeSelectorGroup.tsx accept attribute.
 */
export const SUPPORTED_GEO_EXTENSIONS = [
	".shp",
	".shp.xml",
	".shx",
	".dbf",
	".prj",
	".cpg",
	".sbn",
	".sbx",
	".json",
	".geojson",
	".kml",
	".kmz",
	".zip",
] as const;

/**
 * Types of validation issues that can occur during geodata import.
 */
export type ValidationIssueType = "unsupported_extension" | "parse_failed" | "empty_file" | "invalid_geometry";

/**
 * Represents a warning about a file that could not be processed.
 */
export interface FileValidationWarning {
	/** The name or path of the file */
	fileName: string;
	/** The type of issue encountered */
	issueType: ValidationIssueType;
	/** Human-readable message describing the issue */
	message: string;
}

/**
 * Extracts all file names from a list of files, including contents of ZIP files.
 * For ZIP files, extracts the names of all files inside (prefixed with ZIP name).
 * For non-ZIP files, returns the file name directly.
 *
 * @param files Array of File objects
 * @returns Promise resolving to array of all file names (including ZIP contents)
 */
export async function extractAllFileNames(files: File[]): Promise<string[]> {
	const allFileNames: string[] = [];

	for (const file of files) {
		if (file.name.toLowerCase().endsWith(".zip")) {
			// Extract file names from inside the ZIP
			try {
				const zipData = await file.arrayBuffer();
				const zip = await JSZip.loadAsync(zipData);
				zip.forEach((relativePath) => {
					// Skip directories and macOS metadata files
					if (!relativePath.endsWith("/") && !relativePath.includes("__MACOSX/")) {
						allFileNames.push(`${file.name}/${relativePath}`);
					}
				});
			} catch {
				// If we can't read the ZIP, it will be caught later in parsing
				// Still add the ZIP file name itself so it can be tracked
				allFileNames.push(file.name);
			}
		} else {
			allFileNames.push(file.name);
		}
	}

	return allFileNames;
}

/**
 * Detects files with unsupported extensions from a list of file names.
 *
 * @param fileNames Array of file names (can include paths like "folder/file.txt")
 * @returns Array of FileValidationWarning for each unsupported file
 */
export function detectUnsupportedFiles(fileNames: string[]): FileValidationWarning[] {
	const warnings: FileValidationWarning[] = [];

	for (const fileName of fileNames) {
		const isSupported = SUPPORTED_GEO_EXTENSIONS.some((ext) => fileName.toLowerCase().endsWith(ext));

		if (!isSupported) {
			warnings.push({
				fileName,
				issueType: "unsupported_extension",
				message: `"${fileName}" is not a supported geodata file type.`,
			});
		}
	}

	return warnings;
}

/**
 * Categorizes the result of a GeoData.toGeoJSON() call to determine if there's an issue.
 *
 * @param fileName The name of the file that was processed
 * @param result The result from GeoData.toGeoJSON() (can be null, undefined, or GeoJSON)
 * @param error Optional error that was thrown during parsing
 * @returns FileValidationWarning if there's an issue, null if the result is valid
 */
export function categorizeGeoDataResult(
	fileName: string,
	result: unknown,
	error?: Error,
): FileValidationWarning | null {
	// Handle explicit error case
	if (error) {
		return {
			fileName,
			issueType: "parse_failed",
			message: `Failed to parse "${fileName}": ${error.message}`,
		};
	}

	// Handle null/undefined result
	if (result === null || result === undefined) {
		return {
			fileName,
			issueType: "parse_failed",
			message: `Failed to parse "${fileName}". The file may be corrupted or in an unsupported format.`,
		};
	}

	// Check if it's a valid GeoJSON with features
	const geoJSON = result as { features?: unknown[] };

	if (!geoJSON.features || !Array.isArray(geoJSON.features)) {
		return {
			fileName,
			issueType: "parse_failed",
			message: `Failed to parse "${fileName}". Invalid GeoJSON structure.`,
		};
	}

	// Check for empty features array
	if (geoJSON.features.length === 0) {
		return {
			fileName,
			issueType: "empty_file",
			message: `"${fileName}" contains no features.`,
		};
	}

	// Check for features with invalid or missing coordinates
	const invalidFeatureCount = countFeaturesWithInvalidCoordinates(geoJSON.features);
	if (invalidFeatureCount > 0) {
		const allInvalid = invalidFeatureCount === geoJSON.features.length;
		return {
			fileName,
			issueType: "invalid_geometry",
			message: allInvalid
				? `"${fileName}" contains ${invalidFeatureCount} feature${invalidFeatureCount !== 1 ? "s" : ""} with missing or invalid coordinates.`
				: `"${fileName}" contains ${invalidFeatureCount} of ${geoJSON.features.length} features with missing or invalid coordinates.`,
		};
	}

	// Valid GeoJSON with features - no warning
	return null;
}

/**
 * Counts the number of features with invalid or missing coordinates.
 */
function countFeaturesWithInvalidCoordinates(features: unknown[]): number {
	let invalidCount = 0;

	for (const feature of features) {
		if (!isValidFeature(feature)) {
			invalidCount++;
		}
	}

	return invalidCount;
}

/**
 * Checks if a GeoJSON feature has valid geometry with coordinates.
 */
function isValidFeature(feature: unknown): boolean {
	if (!feature || typeof feature !== "object") return false;

	const f = feature as { geometry?: unknown };
	if (!f.geometry || typeof f.geometry !== "object") return false;

	const geometry = f.geometry as { type?: string; coordinates?: unknown };
	if (!geometry.type || geometry.coordinates === undefined || geometry.coordinates === null) return false;

	// Validate coordinates based on geometry type
	return hasValidCoordinates(geometry.type, geometry.coordinates);
}

/**
 * Recursively validates coordinates based on geometry type.
 */
function hasValidCoordinates(geometryType: string, coordinates: unknown): boolean {
	if (coordinates === null || coordinates === undefined) return false;

	switch (geometryType) {
		case "Point":
			// Point: [lng, lat] or [lng, lat, altitude]
			return isValidPosition(coordinates);

		case "MultiPoint":
		case "LineString":
			// Array of positions
			return Array.isArray(coordinates) && coordinates.length > 0 && coordinates.every(isValidPosition);

		case "MultiLineString":
		case "Polygon":
			// Array of arrays of positions
			return (
				Array.isArray(coordinates) &&
				coordinates.length > 0 &&
				coordinates.every((ring) => Array.isArray(ring) && ring.length > 0 && ring.every(isValidPosition))
			);

		case "MultiPolygon":
			// Array of polygons
			return (
				Array.isArray(coordinates) &&
				coordinates.length > 0 &&
				coordinates.every(
					(polygon) =>
						Array.isArray(polygon) &&
						polygon.length > 0 &&
						polygon.every((ring) => Array.isArray(ring) && ring.length > 0 && ring.every(isValidPosition)),
				)
			);

		case "GeometryCollection":
			// Not typical, but handle gracefully
			return true;

		default:
			return false;
	}
}

/**
 * Checks if a value is a valid GeoJSON position [lng, lat] or [lng, lat, altitude].
 */
function isValidPosition(position: unknown): boolean {
	if (!Array.isArray(position) || position.length < 2) return false;

	const [lng, lat] = position;
	return typeof lng === "number" && typeof lat === "number" && !isNaN(lng) && !isNaN(lat);
}

/**
 * Determines if a file is a shapefile component based on extension.
 */
export function isShapefile(fileName: string): boolean {
	return (
		fileName.endsWith(".shp") || fileName.endsWith(".shx") || fileName.endsWith(".dbf") || fileName.endsWith(".prj")
	);
}

/**
 * Aggregates auto-guessed property mappings from all groups in the geoDataMap.
 * Collects all unique guessed values per field and maps them to PropertyWithExamples.
 *
 * @param geoDataMap - Map of group keys to GeoJSON data arrays
 * @param availableProperties - Array of properties with examples (for enriching results)
 * @returns Aggregated auto-guessed mappings, or null if geoDataMap is empty
 */
export function aggregateAutoGuessedMappings(
	geoDataMap: GeoJSONDataMap,
	availableProperties: PropertyWithExamples[],
): AggregatedAutoGuessResult | null {
	if (geoDataMap.size === 0) {
		return null;
	}

	// Collect auto-guesses from each group
	const allGuesses: Array<ReturnType<typeof autoGuessPropertyMappings>> = [];

	for (const [, geoDataList] of geoDataMap) {
		// Extract GeoJSON from each group
		const geoJsons = geoDataList.map((d) => d.geoJSON);

		// Get auto-guessed mappings for this group
		const groupGuess = autoGuessPropertyMappings(geoJsons);
		allGuesses.push(groupGuess);
	}

	// Aggregate unique values per field
	const fields = [
		"grower",
		"farm",
		"field",
		"fieldId",
		"labName",
		"labInstructions",
		"submissionNotes",
		"yieldGoal",
		"submitterName",
		"submitterEmail",
		"growerPhoneNumber",
		"eventId",
		"labSubmissionCode",
		"externalId",
		"externalFarmId",
		"externalLabId",
		"dateFieldReadyEstimated",
		"density",
		"depth",
		"frequencyOfMicros",
		"testPackage",
		"fieldPriority",
		"nextCrop",
		"lastCrop",
		"branch",
		"salesperson",
		"salespersonMobilePhone",
	] as const;
	const result: AggregatedAutoGuessResult = {
		grower: null,
		farm: null,
		field: null,
		fieldId: null,
		labName: null,
		labInstructions: null,
		submissionNotes: null,
		yieldGoal: null,
		submitterName: null,
		submitterEmail: null,
		growerPhoneNumber: null,
		eventId: null,
		labSubmissionCode: null,
		externalId: null,
		externalFarmId: null,
		externalLabId: null,
		dateFieldReadyEstimated: null,
		density: null,
		depth: null,
		frequencyOfMicros: null,
		testPackage: null,
		fieldPriority: null,
		nextCrop: null,
		lastCrop: null,
		branch: null,
		salesperson: null,
		salespersonMobilePhone: null,
	};

	for (const field of fields) {
		// Collect all non-null guesses for this field
		const guessSet = new Set<string>();

		for (const guess of allGuesses) {
			const value = guess[field];
			if (value !== null) {
				guessSet.add(value);
			}
		}

		if (guessSet.size === 0) {
			result[field] = null;
		} else {
			// Map to PropertyWithExamples objects
			const propertyOptions: PropertyWithExamples[] = Array.from(guessSet).map((propName) => {
				const prop = availableProperties.find((p) => p.name === propName);
				return prop || { name: propName, examples: [] };
			});
			result[field] = propertyOptions;
		}
	}

	return result;
}
